import { z } from 'zod';
import { createAggregateQuerySchema, createAggregateResponseSchema } from './aggregate.js';
import { CalendarDateOnlySchema, CalendarDateSchema, LowercaseUuidSchema, PaginationQuerySchema, SearchTermSchema, UnitTypeSchema, createPaginatedResponseSchema, saVatNumberSchema, writeWarningsFields } from './common.js';
import { PaymentFundingSourceSchema } from './document-payment.js';
import { nullableText } from '../sanitised-text.js';
// =============================================================================
// Invoice
// =============================================================================
export const InvoiceStatusSchema = z.enum(['DRAFT', 'APPROVED', 'SENT', 'PAID', 'VOIDED']);
// Derived payment state, a separate axis from the lifecycle status above.
// Computed server-side from recorded-payment allocations vs the invoice total;
// never stored. AWAITING_PAYMENT = nothing recorded; PARTIALLY_PAID = some but
// not all; PAID = fully settled (or the observational fallback: the legacy /
// ledger-settled stored status is already 'PAID').
//
// The lifecycle 'PAID' status is being deprecated in favour of this axis (the
// eventual lifecycle terminals are issued/voided); until then both coexist and
// the stored status is kept in sync by the invoice payment listener.
export const InvoicePaymentStatusSchema = z.enum(['AWAITING_PAYMENT', 'PARTIALLY_PAID', 'PAID']);
export const InvoiceSortBySchema = z.enum([
    'date', 'dueDate', 'totalMinorUnits', 'clientName', 'status', 'invoiceNumber', 'reference',
]);
export const InvoiceSortDirectionSchema = z.enum(['asc', 'desc']);
export const InvoiceLineItemSchema = z.object({
    id: z.string(),
    invoiceId: z.string(),
    description: z.string(),
    date: z.string().nullable(),
    units: z.number().nullable(),
    pricePerUnitMinorUnits: z.number().nullable(),
    totalAmountMinorUnits: z.number(),
    vatRateBasisPoints: z.number(),
    // Server-derived exact VAT for from-expense and gross direct lines.
    // Null derives from the rate; callers cannot supply arbitrary exact VAT.
    vatAmountMinorUnits: z.number().int().nullable(),
    performedByName: z.string().nullable(),
    isDisbursement: z.boolean(),
    unitType: z.string().nullable(),
    ledgerAccountId: z.string().nullable().optional(),
    // Null on every legacy or manually-entered line. Non-null only when the line
    // was created via POST .../line-items/from-expense (Option B pull billing).
    // Derives the supplier invoice's billing status: BILLED iff non-null.
    supplierInvoiceId: z.string().nullable(),
    createdAt: z.string(),
    activities: z.array(z.object({
        id: z.string(),
        clientProjectId: z.string(),
        date: z.string().datetime(),
        // Who recorded the activity; null on legacy rows and when the creator's
        // user was deleted. No web consumer reads it since any member may archive
        // an activity they can see; remove it with the backend select and mapper.
        createdByUserId: z.string().nullable().optional(),
        // Fee earner on the activity. The invoice stamp defaults a line's account
        // from it when every activity on the line names the same one.
        performedByUserId: z.string().nullable().optional(),
    })),
});
/**
 * The invoice's currently-live pro forma (sent, not withdrawn), derived
 * server-side - never stored as its own column. Present only on a DRAFT
 * invoice; APPROVED/SENT/PAID/VOIDED always read `liveProforma: null` even if
 * an earlier draft state once carried one (withdrawn on approval's own draft
 * -> non-draft transition is not a thing; a live pro forma only exists while
 * DRAFT). `matchesInvoice` - whether the invoice's current composition and
 * money figures still agree with the pro forma's own snapshot - is populated
 * wherever the reader already holds the full per-line invoice with ids: the
 * detail read, and every other `getInvoice` caller, including the send /
 * mark-sent / withdraw mutation responses. List rows omit it (the list's
 * lighter line-item projection carries no per-line ids to compare against).
 */
export const LiveProformaSchema = z.object({
    id: z.string(),
    proformaNumber: z.string().regex(/^PF-\d{4}$/),
    sentAt: z.string(),
    matchesInvoice: z.boolean().optional(),
});
export const INVOICE_NULLABLE_CLIENT_HEADERS = { 'X-Bill-Invoice-Nullable-Client': '1' };
export const InvoiceSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    clientId: z.string().nullable(),
    // The matter this invoice bills; null = client level (legacy invoices, and
    // drafts started before a matter is chosen). Every serialiser sets it.
    clientProjectId: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    date: z.string(),
    dueDate: z.string().nullable(),
    status: InvoiceStatusSchema,
    currency: z.string(),
    subtotalMinorUnits: z.number(),
    taxAmountMinorUnits: z.number(),
    vatRateBasisPoints: z.number(),
    totalMinorUnits: z.number(),
    notes: z.string().nullable(),
    reference: z.string().nullable(),
    subjectLine: z.string().nullable().optional(),
    paymentReference: z.string().nullable(),
    bankAccountId: z.string().nullable(),
    ledgerEntrySetId: z.string().nullable().optional(),
    // Derived payment fields (server-computed, never stored). settled is the sum
    // of recorded-payment allocations; credited is the sum of live (non-voided)
    // credit notes issued against the invoice - a separate term, never folded
    // into settled; outstanding = max(0, total - settled - credited), clamped to
    // 0 when paymentStatus is PAID (so a legacy/ledger-settled invoice with no
    // allocations still reads 0 outstanding). paymentStatus is derived via
    // deriveInvoicePaymentFields (invoice.helpers.ts).
    //
    // Optional because of the SERIALISER a route reaches for, not the invoice's
    // status. Every path through `withPaymentFields` populates them - it spreads
    // `deriveInvoicePaymentFields` on both its DRAFT and its non-DRAFT branch - so
    // the list and detail reads carry them, and so do approve and void, which both
    // return through it. What omits them is `formatInvoice`, the plain row
    // serialiser the create and update routes use, which emits no payment fields
    // at all. Consumers default an absent value to AWAITING_PAYMENT / 0.
    settledAmountMinorUnits: z.number().int().optional(),
    creditedAmountMinorUnits: z.number().int().optional(),
    writtenOffAmountMinorUnits: z.number().int().optional(),
    outstandingAmountMinorUnits: z.number().int().optional(),
    paymentStatus: InvoicePaymentStatusSchema.optional(),
    // Non-null = the draft was deleted (soft-delete). The UI renders these greyed with a
    // "Deleted" status. Always null for APPROVED/SENT/PAID/VOIDED invoices.
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    clientName: z.string().nullable(),
    // Optional exactly like paymentStatus: populated by the read paths that
    // resolve it (detail, list, and the mutation responses that call getInvoice),
    // absent on the lighter create/update responses. See LiveProformaSchema.
    liveProforma: LiveProformaSchema.nullable().optional(),
});
/**
 * One pro forma in an invoice's history (generated, sent, or withdrawn).
 * `documentFormat` is the stored document's format, `null` when none is stored
 * (a legacy row, or one whose best-effort upload failed at generation time).
 * `changedSinceGenerated` is what has changed on the invoice since this pro
 * forma's own snapshot: `none`, only money `figures`, or the `lines`
 * themselves - `null` when that cannot be told (no snapshot, or the read did
 * not derive it). Only a stored PDF with `none` can be emailed by
 * `.../:proformaInvoiceId/send`; "mark as sent" is refused only on `lines`.
 */
export const ProformaSummarySchema = z.object({
    id: z.string(),
    proformaNumber: z.string().regex(/^PF-\d{4}$/),
    createdAt: z.string(),
    sentAt: z.string().nullable(),
    withdrawnAt: z.string().nullable(),
    documentFormat: z.enum(['pdf', 'docx']).nullable(),
    changedSinceGenerated: z.enum(['none', 'figures', 'lines']).nullable(),
});
/**
 * The invoice's composition and money figures at the moment a pro forma was
 * issued (generated, or - for a legacy generated-only row - first marked as
 * sent). Stored as `ProformaInvoice.compositionSnapshot`; see that column's
 * schema comment. `lineItems` carries just enough per-line state to answer
 * "did the composition change" (the id set) and "did the figures change"
 * (everything else) without re-deriving VAT.
 */
export const ProformaCompositionSnapshotLineItemSchema = z.object({
    id: z.string(),
    units: z.number().nullable(),
    pricePerUnitMinorUnits: z.number().nullable(),
    vatRateBasisPoints: z.number(),
    totalAmountMinorUnits: z.number(),
    isDisbursement: z.boolean(),
});
export const ProformaCompositionSnapshotSchema = z.object({
    currency: z.string(),
    subtotalMinorUnits: z.number(),
    taxAmountMinorUnits: z.number(),
    totalMinorUnits: z.number(),
    lineItems: z.array(ProformaCompositionSnapshotLineItemSchema),
});
/** Body for `POST .../proforma-invoices/:proformaInvoiceId/send`. */
export const SendProformaInputSchema = z.object({
    idempotencyKey: z
        .string()
        .min(1, 'Idempotency key is required')
        .max(255, 'Idempotency key must be 255 characters or less'),
    to: z.array(z.email()).optional(),
    cc: z.array(z.email()).optional(),
    bcc: z.array(z.email()).optional(),
    bccSender: z.boolean().optional(),
});
// A single recorded payment surfaced on an invoice's detail view. amount is the
// portion allocated to THIS invoice. Used to render the payment history and the
// per-payment undo (void).
export const InvoiceRecordedPaymentSchema = z.object({
    id: z.string(),
    amountMinorUnits: z.number().int(),
    paidAt: z.string(),
    reference: z.string().nullable(),
    createdAt: z.string(),
    /**
     * True once the payment has been reconciled to a bank statement line (drained
     * from Payments Clearing). Reconciled payments cannot be voided directly from
     * the invoice - the bank line must be unmatched first - so the UI hides the
     * per-payment undo for them.
     */
    reconciled: z.boolean(),
    /**
     * The bank statement line that settled this payment, when it was reconciled
     * (drained from Payments Clearing); null for an unreconciled or document-free
     * mint. The raw link behind `reconciled`, exposed so a caller can resolve
     * which line to unmatch without inferring it through Payments Clearing.
     */
    bankStatementLineId: z.string().uuid().nullable(),
    isWriteOff: z.boolean(),
    /**
     * Non-null when this payment record was minted purely to fund a credit-note
     * application (an AR-to-AR contra, no cash) rather than a real cash payment
     * - see `Payment.fundingCreditNoteId`. The invoice detail UI uses this to
     * render a distinct "Credit applied" row instead of a generic payment row.
     */
    fundingCreditNoteId: z.string().nullable(),
    /** The funding credit note's display number, when resolvable (e.g. "CN-0002"). */
    fundingCreditNoteNumber: z.string().nullable(),
    /**
     * Which source funded this payment's settlement leg. Null for
     * legacy or non-ledgered payments. `CREDIT_NOTE` and `CLIENT_CREDIT` both
     * render as the "Credit applied" row - distinguished by
     * `fundingCreditNoteNumber` (CN) vs its absence (overpayment remainder).
     */
    fundingSource: PaymentFundingSourceSchema.nullable(),
});
/**
 * The client + firm + bank identity frozen onto an issued invoice at approve.
 * Present on the detail response only for a non-DRAFT invoice that carries a
 * snapshot; null for DRAFT (a live preview), a pro forma, or a legacy row the
 * backfill never reached. Addresses are the raw parts (the UI renders them as
 * separate lines, exactly as it does the live client). The frontend reads this
 * for an issued invoice so the on-screen document matches the frozen PDF; when
 * null it falls back to the live client / account / bank read.
 */
export const InvoiceIdentitySnapshotSchema = z.object({
    clientName: z.string(),
    clientLegalName: z.string().nullable(),
    clientVatNumber: z.string().nullable(),
    clientAddressLine1: z.string().nullable(),
    clientAddressLine2: z.string().nullable(),
    clientAddressCity: z.string().nullable(),
    clientAddressProvince: z.string().nullable(),
    clientAddressPostalCode: z.string().nullable(),
    clientAddressCountry: z.string().nullable(),
    fromName: z.string().nullable(),
    fromVatNumber: z.string().nullable(),
    fromRegistrationNumber: z.string().nullable(),
    fromAddressLine1: z.string().nullable(),
    fromAddressLine2: z.string().nullable(),
    fromAddressCity: z.string().nullable(),
    fromAddressProvince: z.string().nullable(),
    fromAddressPostalCode: z.string().nullable(),
    fromAddressCountry: z.string().nullable(),
    bankName: z.string().nullable(),
    bankBranchCode: z.string().nullable(),
    bankAccountNumber: z.string().nullable(),
    bankAccountHolderName: z.string().nullable(),
});
export const InvoiceWithLineItemsSchema = InvoiceSchema.extend({
    lineItems: z.array(InvoiceLineItemSchema),
    // Issue-time identity snapshot; null for DRAFT / pro forma / legacy rows. See
    // InvoiceIdentitySnapshotSchema.
    identitySnapshot: InvoiceIdentitySnapshotSchema.nullable().optional(),
    xeroExport: z.object({
        xeroInvoiceId: z.string(),
        xeroInvoiceUrl: z.string().nullable(),
    }).nullable(),
    proformas: z.array(ProformaSummarySchema),
    // Recorded payments allocated to this invoice. Populated on the detail read;
    // absent on the lighter create/update/list responses.
    payments: z.array(InvoiceRecordedPaymentSchema).optional(),
});
// Approval reports conduct concerns as warnings on a write that succeeds. The
// server always emits both fields, empty when there is nothing to report.
export const ApproveInvoiceResponseSchema = InvoiceWithLineItemsSchema.extend(writeWarningsFields);
/** Querystring for `GET .../invoices/by-line-item`. Capped at 100 ids per call. */
export const InvoicesByLineItemQuerySchema = z.object({
    lineItemIds: z.array(z.string()).min(1).max(100),
});
/**
 * One invoice as returned by `GET .../invoices/by-line-item`: enough to
 * deeplink (id + number) and to lock picker rows (`liveProforma`). Callers
 * that need line items fetch the invoice detail.
 */
export const InvoiceLineItemCoverSchema = z.object({
    id: z.string(),
    invoiceNumber: z.string().nullable(),
    liveProforma: LiveProformaSchema.nullable(),
});
/**
 * Response for `GET .../invoices/by-line-item?lineItemIds=`: resolves which
 * invoice each requested line-item id sits on. `lineItems` carries one entry
 * per KNOWN id - an id the caller has no access to, or that does not exist,
 * is simply absent, never an error. Feeds the activity "this work is on
 * invoice INV-NNNN" alert/deeplink, the draft-editor lock partition, and the
 * omnibox.
 */
export const InvoicesByLineItemResponseSchema = z.object({
    lineItems: z.array(z.object({ lineItemId: z.string(), invoiceId: z.string() })),
    invoices: z.array(InvoiceLineItemCoverSchema),
});
export const WriteOffInvoiceInputSchema = z.object({
    writtenOffAt: CalendarDateSchema.optional(),
    // Required, like every resource-creating POST writer: the write-off records a
    // BAD_DEBTS-funded payment, so the caller-supplied key is the dedup signal
    // that stops a retry or concurrent double-submit posting the write-off twice.
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
});
/** Most activities and expenses one create call may bill together. */
export const CREATE_INVOICE_MAX_INITIAL_LINES = 500;
export const CreateInvoiceInputSchema = z
    .strictObject({
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
    clientId: z.uuid('Client ID must be a UUID').optional(),
    date: CalendarDateSchema,
    dueDate: CalendarDateSchema.optional().nullable(),
    // Omitted = the matter's currency when a matter is named, else ZAR.
    currency: z.string().length(3, 'Currency must be a 3-letter ISO code').optional(),
    notes: nullableText(2000, 'Notes must be 2000 characters or less'),
    clientProjectId: LowercaseUuidSchema.nullable()
        .optional()
        .describe("The matter this invoice bills; needs clientId and must be one of that client's active matters. It fills reference and subjectLine from the matter and, when currency is omitted, sets the currency to the matter's. Omit or null for an invoice with no matter."),
    // Lines to create the draft with, in the same transaction as the invoice: a
    // failed line leaves no invoice behind. The caps bound that transaction; add
    // further lines to the draft through the line-item endpoints.
    activityIds: z
        .array(z.string().min(1))
        .max(CREATE_INVOICE_MAX_INITIAL_LINES, `At most ${CREATE_INVOICE_MAX_INITIAL_LINES} activities can be added at creation`)
        .optional(),
    supplierInvoiceIds: z
        .array(z.uuid('Each supplier invoice ID must be a UUID'))
        .max(CREATE_INVOICE_MAX_INITIAL_LINES, `At most ${CREATE_INVOICE_MAX_INITIAL_LINES} expenses can be added at creation`)
        .optional(),
})
    .refine((input) => (input.activityIds?.length ?? 0) + (input.supplierInvoiceIds?.length ?? 0) <= CREATE_INVOICE_MAX_INITIAL_LINES, {
    message: `At most ${CREATE_INVOICE_MAX_INITIAL_LINES} activities and expenses together can be added at creation`,
    path: ['activityIds'],
});
// `lineItems` is present only when the request created the draft with lines.
export const CreateInvoiceResponseSchema = InvoiceSchema.extend({
    lineItems: z.array(InvoiceLineItemSchema).optional(),
});
// Duplicate an existing invoice into a fresh editable DRAFT. The source is
// addressed by the :invoiceId path param; the idempotency key is required so
// a retried POST returns the same copy rather than a second one. Everything
// copied (client, line items, VAT, GL allocations) is derived server-side
// from the source - nothing about the copy's content is client-supplied.
export const CopyInvoiceInputSchema = z.object({
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
});
// Void an APPROVED invoice and recreate it as an editable DRAFT carrying every
// line item (linked activities and expenses included, original dates kept) so
// a line-item mistake can be fixed and the invoice re-approved. The source is
// addressed by the :invoiceId path param; everything on the reissued draft is
// derived server-side from the source. The idempotency key is required so a
// retried POST returns the same reissued draft rather than voiding twice.
export const ReissueInvoiceInputSchema = z.object({
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
});
// Nullable free-text fields go through `nullableText`, which normalises "" -> null.
// The api-framework runs Zod `.parse` after AJV so the transform fires on HTTP too,
// not just direct-parse callers. Callers may send `null` to clear a value or `""`
// as a form-UX equivalent; both land as `null` in the domain and DB.
export const UpdateInvoiceInputSchema = z.object({
    date: CalendarDateSchema.optional(),
    dueDate: CalendarDateSchema.optional().nullable(),
    notes: nullableText(2000, 'Notes must be 2000 characters or less'),
    reference: nullableText(100, 'Reference must be 100 characters or less'),
    subjectLine: nullableText(200, 'Matter name must be 200 characters or less'),
    paymentReference: nullableText(100, 'Payment reference must be 100 characters or less'),
    // Send a UUID to set, null to clear, or omit to leave unchanged. Empty
    // strings are not accepted - frontend mutations always send a real UUID
    // via the bank-account picker.
    bankAccountId: z.uuid('bankAccountId must be a UUID').optional().nullable(),
    // Override the auto-assigned invoice number for this draft. Same bounds
    // as UpdateInvoiceSettingsInputSchema.nextInvoiceNumber.
    invoiceNumberSequence: z.int().min(1).max(99_999_999).optional(),
});
/**
 * Request body for the super-admin `POST .../set-reference` endpoint, which
 * sets ONLY the `reference` field, on an invoice of any non-VOIDED status.
 * The generic `UpdateInvoiceInputSchema.reference` above only ever applies
 * while the invoice is DRAFT; this narrower endpoint is how a reference gets
 * corrected on an already-issued invoice.
 *
 * Reuses the same `nullableText(100)` shape as `UpdateInvoiceInputSchema.reference`
 * (an empty string normalises to `null`), but `.nonoptional()`: unlike the PATCH
 * field, `reference` is required here - there is no "leave unchanged" meaning
 * for a single-field endpoint, so the caller always states the intended value
 * (`null` clears it).
 */
export const SetInvoiceReferenceInputSchema = z.object({
    reference: nullableText(100, 'Reference must be 100 characters or less').nonoptional(),
});
export const AddLineItemInputSchema = z.object({
    activityId: z.string().min(1, 'Activity ID is required'),
});
export const AddLineItemsInputSchema = z.object({
    activityIds: z.array(z.string().min(1)).min(1, 'At least one activity ID is required'),
});
export const AddDirectLineItemInputSchema = z.object({
    // Omitted means net; gross preserves the payable total and derives exact VAT server-side.
    amountBasis: z.enum(['net', 'gross']).optional(),
    idempotencyKey: z.string().min(1, 'Idempotency key is required').max(255, 'Idempotency key must be 255 characters or less'),
    description: z.string().min(1, 'Description is required').max(2000, 'Description must be 2000 characters or less'),
    // Cap at PostgreSQL Int max (matches the InvoiceLineItem.totalAmountMinorUnits column type).
    // Without this, a caller could submit Number.MAX_SAFE_INTEGER and corrupt downstream sums.
    totalAmountMinorUnits: z.number().int('Total amount must be an integer').min(0, 'Total amount may not be negative').max(2_147_483_647, 'Total amount exceeds the maximum allowed value'),
    // Allow 0 for the "unbillable work shown for transparency" case (matches the activity flow).
    units: z.number().min(0, 'Units may not be negative').optional().nullable(),
    pricePerUnitMinorUnits: z.number().int('Price per unit must be an integer').min(0, 'Price per unit may not be negative').max(2_147_483_647, 'Price per unit exceeds the maximum allowed value').optional().nullable(),
    date: CalendarDateSchema.optional().nullable(),
    isDisbursement: z.boolean().optional().default(false),
    // Free-text performer, for someone who is not a fee earner of the firm. Not
    // with performedByUserId (400).
    performedByName: nullableText(200, 'Performer name must be 200 characters or less'),
    // A current fee earner of the firm. The line stores their profile name and
    // defaults to their own fee income account. Not with performedByName (400).
    performedByUserId: z.uuid('performedByUserId must be a UUID').nullable().optional(),
    unitType: UnitTypeSchema.optional().default('lump_sum'),
    vatRateBasisPoints: z.number().int('VAT rate must be an integer').min(0).max(10000).optional(),
    // Optional GL account allocation set at creation when the account runs a ledger.
    // Omitted or null defaults a disbursement to the DEFAULT_RECOVERY account
    // (Recovery Income) when the ledger has one; any other line, and a
    // disbursement on a ledger without it, takes the performedByUserId fee
    // earner's own default fee account when one is set, else DEFAULT_FEE (active
    // accounts only). A non-null id allocates that account explicitly - the way to
    // put a genuine cost recovery on Disbursements Recoverable. When the account
    // has no active ledger the line stays unallocated. Use /allocate to change or
    // clear the allocation afterward.
    ledgerAccountId: z.string().uuid().nullable().optional(),
});
/**
 * Input schema for adding a discount line to a draft invoice.
 *
 * - `amountMinorUnits`: caller provides a negative value (e.g. -50000 for a R500.00 discount).
 *   Written through to `InvoiceLineItem.totalAmountMinorUnits` as-is. Discount lines are
 *   the only path that may produce a negative line-item total.
 * - `vatRateBasisPoints`: optional compatibility assertion; must match the effective
 *   rate derived from the discounted source lines. It cannot override their VAT.
 * - `idempotencyKey`: required. Prevents duplicate writes on retried requests.
 *   The cache key is composed with `accountId` per the platform-wide rule
 *   (`apps/backend/platform/src/api/CLAUDE.md`, Idempotency-Key section):
 *   `add-discount-line-{accountId}-{idempotencyKey}`.
 */
export const AddDiscountLineInputSchema = z.object({
    /** Atomically replace all existing discount rows instead of adding another. */
    replaceExisting: z.boolean().optional(),
    // Negative-only contract: the asymmetry rule ("discount lines are the only path that may
    // produce a negative line-item total") is enforced at the schema boundary.
    // Business cap: -999_999_999 = R9,999,999.99 in minor units, matching the frontend's
    // 9,999,999.99 major-unit cap in the discount line item input.
    amountMinorUnits: z.number().int('Amount must be an integer').max(-1, 'Discount amount must be negative').min(-999_999_999, 'Amount is below the maximum allowed discount'),
    description: z.string().min(1).max(2000),
    vatRateBasisPoints: z.number().int().min(0).max(10000).optional(),
    idempotencyKey: z.string().min(1, 'Idempotency key is required').max(255, 'Idempotency key must be 255 characters or less'),
});
export const UpdateLineItemInputSchema = z.object({
    description: z.string().min(1, 'Description must not be blank').max(2000, 'Description must be 2000 characters or less').optional(),
    units: z.number().min(0, 'Units may not be negative').max(99999, 'Units must be 99,999 or less').optional(),
    pricePerUnitMinorUnits: z.number().int('Price per unit must be an integer').min(0).max(2_147_483_647).optional(),
    date: CalendarDateSchema.optional().nullable(),
    unitType: UnitTypeSchema.optional(),
    isDisbursement: z.boolean().optional(),
    // `null` clears the performer on an activity-backed line, fee or disbursement;
    // on manual lines the caller may also clear `performedByName` directly.
    performedByUserId: z.uuid('performedByUserId must be a UUID').nullable().optional(),
    performedByName: nullableText(200, 'Performer name must be 200 characters or less'),
    vatRateBasisPoints: z.number().int('VAT rate must be an integer').min(0).max(10000).optional(),
});
export const AllocateInvoiceLineItemInputSchema = z.object({
    // Null clears the explicit allocation and falls back to systemRole defaults.
    ledgerAccountId: z.string().nullable(),
});
export const MoveLineItemInputSchema = z.object({
    activityId: z.string().min(1, 'Activity ID is required'),
});
export const MoveLineItemsInputSchema = z.object({
    activityIds: z.array(z.string().min(1)).min(1, 'At least one activity ID is required'),
});
export const RemoveLineItemsInputSchema = z.object({
    lineItemIds: z.array(z.string().min(1)).min(1, 'At least one line item ID is required'),
});
/**
 * Explicit desired order of one section's lines (fees or disbursements) on one
 * calendar day of a draft invoice. `date` null selects the undated group.
 * `lineItemIds` must be exactly that section's lines for that day, first to
 * last; the full order rather than a move delta keeps a retry harmless.
 */
export const ReorderLineItemsForDayInputSchema = z.strictObject({
    date: CalendarDateOnlySchema.nullable(),
    lineItemIds: z
        .array(LowercaseUuidSchema)
        .min(2, 'At least two line item IDs are required')
        .max(100, 'At most 100 line item IDs can be reordered at once'),
});
/**
 * Input schema for adding an approved supplier invoice (expense) directly onto
 * a draft client invoice as a disbursement line (Option B pull billing).
 *
 * - `supplierInvoiceId`: UUID of the APPROVED supplier invoice to bill. Must
 *   belong to the same account. Returns 400 if not APPROVED; 409 if the
 *   expense is already on another line.
 * - `idempotencyKey`: caller-supplied dedup key. Replaying the same key
 *   returns the same invoice rather than creating a second line.
 */
export const AddLineItemFromSupplierInvoiceInputSchema = z.object({
    supplierInvoiceId: z.string().uuid('Supplier invoice ID must be a UUID'),
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
});
/**
 * Input schema for adding multiple approved supplier invoices (expenses) as
 * disbursement line items on a draft client invoice in one call.
 *
 * - `supplierInvoiceIds`: UUIDs of the APPROVED supplier invoices to bill. Each
 *   must belong to the same account. Returns 400 if any is not APPROVED; 409 if
 *   any is already on another line. The batch fails fast on the first invalid
 *   expense – earlier successful additions are persisted.
 * - `idempotencyKey`: caller-supplied dedup key for the batch. Each expense in
 *   the batch receives a per-item key derived from this key + its own ID, so
 *   replaying the same batch with the same key does not create duplicate lines.
 */
export const AddLineItemsFromExpensesInputSchema = z.object({
    supplierInvoiceIds: z.array(z.string().uuid('Each supplier invoice ID must be a UUID')).min(1, 'At least one supplier invoice ID is required'),
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
});
// The subset of InvoiceStatusSchema that aggregateInvoices accepts. DRAFT is
// deliberately excluded (see the route's mcp description): an editable draft
// has no authoritative invoice total.
export const InvoiceAggregateStatusSchema = z.enum(['APPROVED', 'SENT', 'PAID', 'VOIDED']);
/**
 * Every FILTER field shared by the invoice list and the invoice totals
 * endpoints, independent of pagination, sort, or (for totals) grouping.
 * `InvoiceListQuerySchema` and `InvoiceAggregateQuerySchema` both extend this
 * shape, so the list never inherits `groupBy`/`thenBy` and the two endpoints
 * cannot drift on what "the same filters" means.
 */
export const InvoiceAggregateFiltersQuerySchema = z.object({
    status: z.array(InvoiceAggregateStatusSchema).min(1).optional(),
    clientId: LowercaseUuidSchema.optional(),
    // Only invoices stamped with this matter. Invoices at client level (no matter)
    // never match; there is no way to filter for them, group by `clientProject`
    // and read the `clientProjectId: null` group instead.
    clientProjectId: LowercaseUuidSchema.optional(),
    dateFrom: CalendarDateOnlySchema.optional(),
    dateTo: CalendarDateOnlySchema.optional(),
});
/**
 * Dimensions `aggregateInvoices` can group by. `client` maps onto the
 * invoice's `clientId` FK column; `clientProject` onto its `clientProjectId`
 * (null for an invoice at client level); `status` is 1:1 with the scalar column.
 */
export const InvoiceAggregateDimensionSchema = z.enum(['client', 'status', 'clientProject']);
export const InvoiceAggregateQuerySchema = createAggregateQuerySchema(InvoiceAggregateFiltersQuerySchema, InvoiceAggregateDimensionSchema);
export const InvoiceAggregateResponseSchema = createAggregateResponseSchema({
    // `count` is the number of invoices. Amounts carry one entry per currency
    // because totals in different currencies must never be added together.
    measures: z.object({
        amounts: z.array(z.object({
            currency: z.string(),
            subtotalMinorUnits: z.int(),
            taxAmountMinorUnits: z.int(),
            totalMinorUnits: z.int(),
        })),
    }),
    key: z.object({
        clientId: z.string().optional(),
        clientName: z.string().optional(),
        // null is the group of invoices at client level, with no matter.
        clientProjectId: z.string().nullable().optional(),
        clientProjectName: z.string().optional(),
        status: InvoiceAggregateStatusSchema.optional(),
    }),
    filter: z.object({
        status: z.array(InvoiceAggregateStatusSchema),
        clientId: z.string().nullable(),
        clientProjectId: z.string().nullable(),
        dateFrom: z.string().nullable(),
        dateTo: z.string().nullable(),
    }),
    dimension: InvoiceAggregateDimensionSchema,
});
export const OutstandingTotalResponseSchema = z.object({
    // The Accounts Receivable control balance on an accounting-enabled account
    // (net of payments, credit notes and unallocated client credits, so it can
    // be negative); the gross sum of open invoice totals otherwise.
    totalMinorUnits: z.int(),
    // Open (APPROVED + SENT) invoices. Status-based while the total is
    // balance-based, so the two need not reconcile.
    openInvoiceCount: z.int(),
    // The ledger's base currency, 'ZAR' when the account has no ledger.
    currency: z.string(),
});
/**
 * The filter and sort vocabulary shared by the invoice list and its exports, so
 * what is exported is selected by exactly the parameters the list takes.
 */
export const InvoiceListFiltersQuerySchema = z.object({
    ...InvoiceAggregateFiltersQuerySchema.shape,
    status: z.array(InvoiceStatusSchema).min(1).optional(),
    // Include soft-deleted (archived) invoices in the result. Modelled as a separate flag
    // rather than a "DELETED" status value because archived rows keep their underlying
    // DRAFT status; "deleted" is derived from archivedAt, not a status member. The frontend
    // "Deleted" filter chip sets this true. When neither `status` nor `includeArchived` is
    // supplied, the list returns everything (live + deleted) inline.
    includeArchived: z.enum(['true', 'false']).optional(),
    search: SearchTermSchema.optional(),
    sortBy: InvoiceSortBySchema.optional().prefault('date'),
    sortDirection: InvoiceSortDirectionSchema.optional().prefault('desc'),
});
// Strict, as every new request schema is (src/api/CLAUDE.md "Request Schemas Are
// Strict"): an export query naming a filter the list does not take is refused
// rather than silently exporting an unfiltered list.
export const InvoiceListExportQuerySchema = z.strictObject({ ...InvoiceListFiltersQuerySchema.shape });
export const InvoiceListQuerySchema = PaginationQuerySchema.extend({
    ...InvoiceListFiltersQuerySchema.shape,
    // Restrict the list to these invoice ids (max 100). Absent or empty means no id filter.
    ids: z.array(z.uuid()).max(100).optional(),
    // Opt-in expansion: when 'true', each row in the list response carries a
    // `lineItems` array (see InvoiceListLineItemSchema below). Omitted or 'false'
    // (the default) returns headers only, with no `lineItems` key - the
    // pre-existing, unchanged shape. The list repository read already joins
    // lineItems on every call (DRAFT running-total overlay needs them); this flag
    // only widens the projection and stops the service from stripping the array.
    includeLineItems: z.enum(['true', 'false']).optional(),
});
// Narrow per-line projection returned only when `includeLineItems=true` on the
// list endpoint. Deliberately six fields - not the 15-field InvoiceLineItemSchema
// used by the detail family (which also carries an `activities[]` relation array) -
// since the list already joins lineItems for every row (DRAFT totals) and a wider
// select or a relation join on every listed invoice would be the exact over-fetch
// this opt-in flag exists to avoid. No `vatAmountMinorUnits`: that field is an
// internal VAT-computation input (see DraftTotalsLineItem in invoice.types.ts),
// not part of this response contract.
export const InvoiceListLineItemSchema = z.object({
    id: z.string(),
    description: z.string(),
    totalAmountMinorUnits: z.number(),
    vatRateBasisPoints: z.number(),
    ledgerAccountId: z.string().nullable(),
    isDisbursement: z.boolean(),
});
// List rows may optionally include a compact line-item projection when
// includeLineItems=true. `pdfStoragePath` (the pointer to the latest stored
// copy) is deliberately kept off this and every other product-facing wire
// contract - no product code reads it. The e2e bypass response
// (E2ESyncInvoiceLineItemResponseSchema below) exposes it separately as a
// read-only observation window that e2e specs read directly.
export const InvoiceListItemSchema = InvoiceSchema.extend({
    lineItems: z.array(InvoiceListLineItemSchema).optional(),
});
export const InvoiceListResponseSchema = createPaginatedResponseSchema(InvoiceListItemSchema);
export const DeleteInvoiceResponseSchema = z.object({
    success: z.boolean(),
});
export const RemoveLineItemResponseSchema = InvoiceWithLineItemsSchema;
export const GenerateInvoiceInputSchema = z.object({
    bankAccountId: z.uuid('bankAccountId must be a UUID').optional().nullable(),
    subjectLine: nullableText(200, 'Matter name must be 200 characters or less').optional(),
});
export const SendInvoiceInputSchema = z.object({
    to: z.array(z.email()).min(1).optional(),
    cc: z.array(z.email()).optional(),
    bcc: z.array(z.email()).optional(),
    bccSender: z.boolean().optional(),
    /** Document IDs selected for attachment alongside the invoice PDF. Only IDs belonging to supplier invoices billed on this invoice are honoured; out-of-scope IDs are silently dropped. Bounded well above any realistic invoice (a handful of disbursement lines, max 20 docs each) to cap server-side allocation on a hostile request. */
    supportingDocumentIds: z.array(z.string().uuid()).max(200).optional(),
    /** Refuses the send (409, `reason: 'stale-version'`) when what it would show the client changed since this fingerprint; a chat button pins it. */
    expectedFingerprint: z.string().min(1).optional(),
});
export const InvoiceSendStatusSchema = z.enum(['PENDING', 'SENT', 'DELIVERED', 'BOUNCED', 'FAILED', 'COMPLAINED']);
export const InvoiceSendSchema = z.object({
    id: z.string(),
    sentAt: z.string().nullable(),
    subject: z.string(),
    status: InvoiceSendStatusSchema,
    to: z.array(z.string()),
    cc: z.array(z.string()),
    bcc: z.array(z.string()),
    // Non-null only for a pro forma send (see SendProformaInputSchema); null for
    // an ordinary invoice send. Lets the send-history UI/agent tell the two apart.
    proformaNumber: z.string().nullable(),
});
export const ListInvoiceSendsResponseSchema = z.object({
    sends: z.array(InvoiceSendSchema),
});
export const InvoiceSettingsResponseSchema = z.object({
    nextInvoiceNumber: z.int(),
});
// =============================================================================
// Invoice - Eligible Attachments manifest
// =============================================================================
/**
 * A single document eligible for attachment to a client invoice email.
 * Part of the `EligibleInvoiceAttachmentsResponse` manifest.
 */
export const EligibleInvoiceAttachmentDocumentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    sizeBytes: z.number(),
});
/**
 * One group in the eligible-attachments manifest. Corresponds to a single
 * disbursement line item whose `supplierInvoiceId` is non-null. `documents`
 * is empty when the supplier invoice has no uploaded document (the dialog
 * renders a greyed "No document attached" hint in that case).
 */
export const EligibleInvoiceAttachmentGroupSchema = z.object({
    lineItemId: z.string(),
    supplierInvoiceId: z.string(),
    description: z.string(),
    documents: z.array(EligibleInvoiceAttachmentDocumentSchema),
});
/**
 * Response for `GET /accounts/:accountId/invoices/:invoiceId/eligible-attachments`.
 *
 * `groups` is empty when the invoice has no disbursement lines backed by a
 * supplier invoice.
 */
export const EligibleInvoiceAttachmentsResponseSchema = z.object({
    groups: z.array(EligibleInvoiceAttachmentGroupSchema),
});
export const UpdateInvoiceSettingsInputSchema = z.object({
    nextInvoiceNumber: z.int().min(1).max(99_999_999),
});
// =============================================================================
// Invoice – Ledger Treatment Endpoints
// =============================================================================
/**
 * Request body for `POST .../opening-retag`.
 *
 * `openAmountMinorUnits` is the outstanding open balance on the invoice at
 * conversion date, in minor units (e.g. 150000 for R1,500.00). Must be a
 * positive integer not exceeding the invoice's `totalMinorUnits`. The service
 * enforces the upper bound; this schema enforces the lower bound and integer
 * constraint.
 *
 * The recognition-journal endpoint (`POST .../recognition-journal`) takes no
 * body – pass an empty object or omit the body entirely.
 */
export const OpeningRetagInputSchema = z.object({
    openAmountMinorUnits: z.number().int().min(1).max(2_147_483_647),
});
/**
 * Request body for `POST .../approve-opening-item`: approve a pre-conversion
 * DRAFT as an opening debtor. `openAmountMinorUnits` is what the client still
 * owed at the ledger's conversion date, at most the invoice total; the server
 * also refuses an amount above the untagged Debtors balance left to itemise.
 */
export const ApproveInvoiceOpeningItemInputSchema = z.strictObject({
    openAmountMinorUnits: OpeningRetagInputSchema.shape.openAmountMinorUnits,
});
/**
 * Request body for `POST .../reallocate` - moves ONE recognised invoice line's
 * income from the GL account it was posted to onto `ledgerAccountId`, by
 * reversing the linked recognition entry set and reposting a replacement
 * recognition journal derived from the re-stamped line.
 *
 * `lineItemId` must belong to the invoice; `ledgerAccountId` is the target GL
 * account. It must be active (not archived) and either an income account or the
 * Disbursements Recoverable account (the one allowed non-income target); the
 * backend rejects any other section (AR, VAT control, bank, equity, expense).
 * `idempotencyKey` scopes retry dedup; a retry with the same key replays the
 * same correction.
 */
export const ReallocateInvoiceIncomeInputSchema = z.object({
    lineItemId: z.string().uuid('Line item ID must be a valid UUID'),
    ledgerAccountId: z.string().uuid('Ledger account ID must be a valid UUID'),
    idempotencyKey: z.string().min(1, 'Idempotency key is required').max(255),
});
// =============================================================================
// Invoice Corrections (VAT Act s20(1B))
// =============================================================================
/**
 * Whitelisted particulars a correction may change on an issued (APPROVED /
 * SENT / PAID) invoice: the 22 identity-snapshot columns (see
 * InvoiceIdentitySnapshotSchema) plus the four presentation fields already
 * editable pre-issue via UpdateInvoiceInputSchema. Financial values (amounts,
 * VAT, `invoiceNumber`, `date`, `currency`, `clientId`, `bankAccountId`,
 * `status`) are deliberately absent from this object's shape - the s20(1B)
 * proviso ("a correction may never change what was charged") is enforced by
 * construction here, not by a runtime allowlist check downstream.
 *
 * Every field is optional (send only the fields you are correcting) and
 * nullable EXCEPT `clientName`. Clearing it would break the identity-snapshot
 * invariant on an already-issued invoice, and the issued-only readers (PDF
 * render, send, Word export, and this service) all throw `internal()` on a
 * null `clientName` rather than falling back - so a cleared value would take
 * the invoice's own document paths down, not degrade them. Bounds mirror the corresponding client / account / bank-account
 * input schemas the values were originally captured through.
 *
 * `vatRateBasisPoints` is deliberately absent even though `fromVatNumber` is
 * correctable: the invoice's stamped VAT rate is derived from the firm's VAT
 * number at approve time and, per the s20(1B) proviso above, a correction
 * must never change what was charged - so correcting a typo in `fromVatNumber`
 * (e.g. a transposed digit) never re-derives or touches the already-stamped
 * rate, intentionally.
 *
 * Line-item particulars are corrected through the sibling `lineItems` array on
 * `CorrectInvoiceParticularsInputSchema`, not through this object - see
 * `CorrectInvoiceLineItemChangesSchema` for which line fields are correctable
 * and why the amount-bearing ones are not.
 */
export const CorrectInvoiceParticularsChangesSchema = z.object({
    clientName: z.string().min(1, 'Client name is required').max(200, 'Client name must be 200 characters or less').optional(),
    clientLegalName: nullableText(300, 'Legal name must be 300 characters or less'),
    clientVatNumber: saVatNumberSchema,
    clientAddressLine1: nullableText(300),
    clientAddressLine2: nullableText(300),
    clientAddressCity: nullableText(100),
    clientAddressProvince: nullableText(100),
    clientAddressPostalCode: nullableText(20),
    clientAddressCountry: nullableText(100),
    fromName: nullableText(300, 'Firm name must be 300 characters or less'),
    fromVatNumber: saVatNumberSchema,
    fromRegistrationNumber: nullableText(50, 'Registration number must be 50 characters or less'),
    fromAddressLine1: nullableText(300),
    fromAddressLine2: nullableText(300),
    fromAddressCity: nullableText(100),
    fromAddressProvince: nullableText(100),
    fromAddressPostalCode: nullableText(20),
    fromAddressCountry: nullableText(100),
    bankName: nullableText(100),
    bankBranchCode: nullableText(20),
    bankAccountNumber: nullableText(34),
    bankAccountHolderName: nullableText(200),
    reference: nullableText(100, 'Reference must be 100 characters or less'),
    subjectLine: nullableText(200, 'Matter name must be 200 characters or less'),
    notes: nullableText(2000, 'Notes must be 2000 characters or less'),
    paymentReference: nullableText(100, 'Payment reference must be 100 characters or less'),
});
/**
 * The correctable-field whitelist as a plain array, derived from the schema
 * above so it cannot drift from it. For a runtime membership check against a
 * caller-supplied key (e.g. a plain-JS caller that bypasses the Zod parse at
 * the HTTP boundary - the seed script calls the service directly) rather than
 * a Zod `.parse()`, which is the right tool for validating a whole object but
 * not for filtering individual keys out of one.
 */
export const INVOICE_CORRECTABLE_FIELDS = CorrectInvoiceParticularsChangesSchema.keyof().options;
/**
 * One line item's correctable particulars, addressed by `lineItemId`.
 *
 * `description` is the line's narration - a VAT Act s20(4)(e) particular
 * ("full and proper description of the goods or services supplied"), and the
 * only correctable particular that also appears in the abridged tax-invoice
 * list (s20(5)(d)), so it survives on invoices under R5 000 where the client
 * and address particulars do not. Correcting it is exactly the s20(1B) case:
 * wrong particulars, correct amount.
 *
 * Every amount-bearing line field is deliberately absent - `units`,
 * `pricePerUnitMinorUnits`, `totalAmountMinorUnits`, `vatRateBasisPoints`,
 * `vatAmountMinorUnits`, `isDisbursement`, `ledgerAccountId`. Changing any of
 * them changes what was charged or where it posted, which is s21 (credit or
 * debit note) territory, not s20(1B). As with the invoice-level schema above,
 * this is enforced by construction here rather than by a runtime allowlist.
 *
 * Note on `units` specifically: it IS a s20(4)(f) particular ("the quantity or
 * volume of the goods or services supplied"), so a units typo is in principle
 * a s20(1B) case and its absence here is not an oversight. It is excluded
 * because in this system units is a computational input to the line total - it
 * cannot move without what was charged moving, which converts the case to s21.
 * Do not add it on the strength of it being a subsection (4) particular.
 *
 * The line's `date` (the day the work was performed) is excluded on judgement
 * rather than statute: it is descriptive rather than amount-bearing, but
 * editing it on an issued invoice reshapes the billed record in a way a
 * narration fix does not. Revisit deliberately if a real case turns up.
 */
export const CorrectInvoiceLineItemChangesSchema = z.object({
    lineItemId: z.uuid(),
    description: z
        .string()
        .min(1, 'Description is required')
        .max(2000, 'Description must be 2000 characters or less'),
});
/**
 * Request body for `POST .../corrections`. `changes` admits only the
 * invoice-level fields above; any other key on the wire is silently stripped
 * by that schema's shape (Zod's default object behaviour), never reaching the
 * service. `lineItems` carries per-line corrections and is optional.
 *
 * `lineItems` sits beside `changes` rather than inside it so the
 * `INVOICE_CORRECTABLE_FIELDS` derivation above (which keys off
 * `CorrectInvoiceParticularsChangesSchema`) stays a flat list of invoice
 * columns and cannot pick up a nested array key.
 *
 * One request is one correction: a caller fixing two narrations and the
 * client's address submits them together and gets a single `InvoiceCorrection`
 * row and a single retained pre-correction PDF, not three of each.
 *
 * At least one field must resolve to an ACTUAL change - a request whose
 * `changes` is empty, carries only unrecognised keys, or carries only values
 * that already match the invoice's current values, AND whose `lineItems` is
 * absent, empty, or carries only already-matching descriptions, is rejected
 * with 400 by the service. The service owns this check because the request
 * schema cannot know the invoice's current persisted values and non-HTTP
 * callers need the same no-op rejection. Zod refinements do run when the HTTP
 * framework reparses ajv-accepted input, but they only see the request itself.
 */
export const CorrectInvoiceParticularsInputSchema = z.object({
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
    changes: CorrectInvoiceParticularsChangesSchema,
    // Bounded like every other batch array in this package (bank-statement,
    // chat, client-statement all cap theirs). Each entry becomes its own
    // sequential UPDATE inside one open transaction, so an unbounded array holds
    // write locks across arbitrarily many round-trips. `changes` needs no cap -
    // its object shape already bounds it at 26 keys.
    lineItems: z.array(CorrectInvoiceLineItemChangesSchema).max(200, 'Cannot correct more than 200 line items at once').optional(),
});
/**
 * One corrected invoice-level field's audit trail entry: the field name (a key
 * of `CorrectInvoiceParticularsChangesSchema`), the value it held before this
 * correction, and the value it holds after. `old` is always the invoice's own
 * stored column: every issued invoice is guaranteed to carry its identity
 * snapshot, and the issued-only readers throw rather than falling back, so
 * there is no live-resolved `old`. (An earlier iteration resolved `old` from a
 * live client/firm/bank read for pre-snapshot rows; that machinery was deleted
 * once the invariant was enforced by construction.)
 *
 * `scope` is OPTIONAL here and is deliberately NOT written by the service.
 * An invoice-scoped entry is stored exactly as it was before line-item
 * corrections existed - `{ field, old, new }`, no discriminator - so every
 * correction row ever written, including all pre-existing ones, is the same
 * shape. Absence of `scope` IS "invoice-scoped"; only the line-item variant
 * tags itself. That matters because `changeSet` is Zod-parsed at read time
 * (see the column comment in invoice.prisma) rather than cast: a required
 * discriminator would have made every pre-existing row fail to parse, and a
 * `.default()` would have made the schema's input and output types differ,
 * which the `api()` client helper forbids on a response schema (it requires
 * `ZodType<T, T>`).
 *
 * Consumers therefore discriminate on `scope === 'LINE_ITEM'` and treat
 * everything else as invoice-scoped, rather than testing for `'INVOICE'`.
 */
const InvoiceFieldCorrectionChangeSchema = z.object({
    scope: z.literal('INVOICE').optional(),
    field: CorrectInvoiceParticularsChangesSchema.keyof(),
    old: z.string().nullable(),
    new: z.string().nullable(),
});
/**
 * One corrected line-item particular's audit trail entry. `lineItemId`
 * addresses the line within the invoice; `field` is a literal rather than an
 * enum because `description` is currently the only correctable line
 * particular (see `CorrectInvoiceLineItemChangesSchema` for why the
 * amount-bearing fields are not), and a literal keeps the union honest if that
 * ever changes.
 *
 * `lineItemId` is `z.string()` rather than `z.uuid()` here, unlike the input
 * schema: this is a READ shape over an append-only audit row, so it must parse
 * whatever was historically written rather than re-validate it. Validation
 * belongs at the write boundary.
 */
const InvoiceLineItemCorrectionChangeSchema = z.object({
    scope: z.literal('LINE_ITEM'),
    lineItemId: z.string(),
    field: z.literal('description'),
    old: z.string().nullable(),
    new: z.string().nullable(),
});
/**
 * One entry in a correction's `changeSet`: either an invoice-level field or a
 * line-item particular.
 *
 * Deliberately `z.union` and NOT `z.discriminatedUnion`: the invoice variant
 * carries no discriminator value at all (see above), which a discriminated
 * union cannot express - it reads the discriminator off the raw input and
 * rejects an absent one outright (verified against zod 4.4 rather than
 * assumed). A plain union tries each member in order, so an untagged entry
 * falls through to the invoice variant. The line-item member is listed first
 * so a well-formed `LINE_ITEM` entry matches on the first attempt rather than
 * after a failed parse.
 */
export const InvoiceCorrectionChangeSchema = z.union([
    InvoiceLineItemCorrectionChangeSchema,
    InvoiceFieldCorrectionChangeSchema,
]);
/**
 * A single correction event on an issued invoice, newest-first in
 * `ListInvoiceCorrectionsResponseSchema`. `hasPriorPdf` gates the "View
 * previous PDF" action (`GET .../corrections/:correctionId/pdf`); the
 * underlying GCS storage key is never exposed on the wire. `correctedByName`
 * is the point-in-time display name of the correcting user (POPIA s11(1)(c)
 * retention, mirroring `InvoiceLineItem.performedByName`) - null only for a
 * correction row written before this column existed.
 */
export const InvoiceCorrectionSchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    correctedByUserId: z.string(),
    correctedByName: z.string().nullable(),
    changes: z.array(InvoiceCorrectionChangeSchema),
    hasPriorPdf: z.boolean(),
});
/** Response for `GET .../corrections` - newest first. */
export const ListInvoiceCorrectionsResponseSchema = z.object({
    corrections: z.array(InvoiceCorrectionSchema),
});
// =============================================================================
// Invoice - Pro Forma DOCX Request Body
// =============================================================================
/**
 * Optional JSON body for the pro forma invoice endpoint when format=docx.
 * The outer `.optional()` is load-bearing: PDF callers that send no body (and
 * no Content-Type) still pass the schema parse step because
 * `z.object({...}).optional().safeParse(undefined)` succeeds.
 */
export const InvoiceProformaDocxBodySchema = z.object({
    statementExtras: z.object({
        depositMinorUnits: z.int().min(0).optional(),
        // `date` is an ISO date-only string (YYYY-MM-DD). The renderer composes the
        // fixed label "Balance brought over from previous account dated DD/MM/YYYY";
        // there is no free-text label.
        broughtForward: z.object({
            date: z.iso.date(),
            amountMinorUnits: z.int().min(0),
        }).optional(),
    }).optional(),
}).optional();
// =============================================================================
// Invoice Word Export - Feature Gate
// =============================================================================
export const InvoiceWordExportBodyFormatSchema = z.enum(['cafriedlander', 'schliemann', 'bchc']);
export const InvoiceWordExportFeatureResponseSchema = z.object({
    feature: z.object({ bodyFormat: InvoiceWordExportBodyFormatSchema }).nullable(),
});
// =============================================================================
// Invoice - E2E Bypass
// =============================================================================
export const E2ESyncInvoiceLineItemParamsSchema = z.object({
    invoiceId: z.uuid(),
});
export const E2ESyncInvoiceLineItemBodySchema = z.object({
    activityId: z.uuid(),
});
// Widens the normal detail response with the private `pdfStoragePath` pointer -
// the read-only observation window e2e tests use to assert on the latest stored
// copy's key (e.g. distinct keys across repeat downloads of an issued invoice)
// without any product route exposing it.
export const E2ESyncInvoiceLineItemResponseSchema = InvoiceWithLineItemsSchema.extend({
    pdfStoragePath: z.string().nullable(),
});
export const SetInvoiceClientInputSchema = z.strictObject({
    clientId: z.uuid(),
    clientProjectId: LowercaseUuidSchema.nullable()
        .optional()
        .describe("The matter the draft bills. A matter of clientId prefills reference and subjectLine that are blank or still the previous matter's. Omit it to keep the current matter when the client is unchanged and clear it when the client changes; null clears it. Changing the matter is refused while the draft holds work from any other matter."),
});
// HTTP-only client changes carry a desired-state snapshot, never a stored operation.
export const InvoiceClientWorkKindSchema = z.enum(['activity', 'expense']);
const workExpectation = {
    id: LowercaseUuidSchema, lineItemId: LowercaseUuidSchema, fromMatterId: LowercaseUuidSchema,
    currency: z.string().min(1).max(10), updatedAt: z.iso.datetime(),
};
const activityExpectation = z.strictObject({ ...workExpectation, kind: z.literal('activity'), status: z.literal('APPROVED') });
const expenseExpectation = z.strictObject({ ...workExpectation, kind: z.literal('expense'), status: z.enum(['APPROVED', 'PAID']) });
export const InvoiceClientWorkExpectationSchema = z.discriminatedUnion('kind', [activityExpectation, expenseExpectation]);
export const InvoiceClientWorkItemSchema = z.discriminatedUnion('kind', [
    activityExpectation.extend({ name: z.string() }), expenseExpectation.extend({ name: z.string() }),
]);
const switchExpectations = {
    clientId: LowercaseUuidSchema, expectedClientId: LowercaseUuidSchema,
    compositionToken: z.string().regex(/^[a-f0-9]{64}$/),
    expectedWork: z.array(InvoiceClientWorkExpectationSchema).min(1).max(500),
};
// `move` moves every work row to one matter of the new client. `remove` with `clientProjectId` also
// sets that matter (null clears it); on the invoice's current client it is the change-matter flow.
export const SwitchInvoiceClientWithWorkInputSchema = z.union([
    z.strictObject({ ...switchExpectations, matterWork: z.strictObject({ mode: z.literal('move'), toClientProjectId: LowercaseUuidSchema }) }),
    z.strictObject({ ...switchExpectations, matterWork: z.strictObject({ mode: z.literal('remove'), clientProjectId: LowercaseUuidSchema.nullable().optional() }) }),
]).superRefine((input, ctx) => {
    const ids = input.expectedWork.map(row => `${row.kind}:${row.id}`);
    if (new Set(ids).size !== ids.length)
        ctx.addIssue({ code: 'custom', path: ['expectedWork'], message: 'Identify each work row once' });
});
export const InvoiceClientWorkQuerySchema = z.strictObject({ cursor: z.string().max(100).optional(), limit: z.coerce.number().int().min(1).max(100).optional() });
export const InvoiceClientWorkPageSchema = z.object({
    items: z.array(InvoiceClientWorkItemSchema), nextCursor: z.string().nullable(),
    expectedClientId: z.string(), compositionToken: z.string(),
});
export const SwitchInvoiceClientWithWorkResponseSchema = z.object({ invoice: InvoiceSchema });
/** Invoice client-lock bypass contract. */
/** Path params shared by the e2e routes that act on one invoice of one account. */
export const E2EAccountInvoiceParamsSchema = z.strictObject({ accountId: LowercaseUuidSchema, invoiceId: LowercaseUuidSchema });
export const E2EHoldInvoiceClientGuardBodySchema = z.strictObject({ holdMs: z.number().int().min(1).max(3000) });
export const E2EHoldInvoiceClientGuardResponseSchema = z.strictObject({ held: z.boolean() });
export const E2ERevertInvoiceBeforeRecognitionCleanupResponseSchema = z.strictObject({ reverted: z.boolean() });
/** Posts an opening item's journal and stops before the approval flip, as an interrupted approval would. */
export const E2EPostOpeningItemBeforeFlipBodySchema = z.strictObject({
    openAmountMinorUnits: OpeningRetagInputSchema.shape.openAmountMinorUnits,
    /** The super-admin whose retry should replay the journal exactly. */
    postedByUserId: LowercaseUuidSchema,
});
export const E2EPostOpeningItemBeforeFlipResponseSchema = z.strictObject({ posted: z.boolean() });
export const E2EClearInvoiceClientProjectResponseSchema = z.strictObject({ cleared: z.boolean() });
